import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef, useMemo, useCallback, Fragment } from 'react';
import ReactDOM from 'react-dom';
import { useSemantle, useCountdown, LANGUAGES, getPastDays, getYesterdayDayKey, loadWinningWord, clearAllStorage, getColorFromPercentile, getDisplayedScore, formatDisplayedScore, THIRD_PARTY_NOTICES, getHintExplanationForGuess } from '@semantle/shared';
import LanguageSelector from './LanguageSelector';
import {
  logEvent,
  logScreenView,
  GameEvents,
  syncWebAnalyticsConsent,
} from './analytics';
import './App.css';

// EU/EEA/UK country codes (ISO 3166-1 alpha-2) — consent required before storage
const EU_EEA_UK_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB'
]);

// Same-origin proxy (server/index.mjs → ipapi.co) avoids browser CORS/403 on direct ipapi requests.
const GEO_API_URL = (() => {
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  return base ? `${base}/api/geo` : '/api/geo';
})();
const GEO_TIMEOUT_MS = 3000;

/** Same shimmer as "keep guessing" on text; gold ★ where the locale string contains ★. */
function renderHintClickStarCueRow(t) {
  const s = t('hintClickStarExtraClue');
  const parts = s.split('★');
  if (parts.length === 1) {
    return (
      <span className="keep-guessing-hint-cue-row">
        <span className="keep-guessing-text">{s}</span>
      </span>
    );
  }
  return (
    <span className="keep-guessing-hint-cue-row">
      {parts.map((part, i) => {
        let text = part;
        if (i > 0) text = text.replace(/^\s+/, '');
        if (i < parts.length - 1) text = text.replace(/\s+$/, '');
        return (
          <Fragment key={i}>
            {text ? <span className="keep-guessing-text">{text}</span> : null}
            {i < parts.length - 1 ? (
              <span className="hint-cue-star-yellow" aria-hidden>
                ★
              </span>
            ) : null}
          </Fragment>
        );
      })}
    </span>
  );
}
const INFO_BANNER_SEEN_KEY = 'logiword_info_banner_seen';
const APP_INSTALL_BANNER_DISMISSED_KEY = 'logiword_app_install_banner_dismissed';
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || 'https://play.google.com/store/apps/details?id=com.sariyanidi.logiword';
const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || 'https://apps.apple.com/us/app/logiword/id6758896197';
const PLAY_STORE_BADGE_URL = 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png';
const APP_STORE_BADGE_URL = 'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg';

// Resolve which flag to show for a language code in settings.
// For English, show 🇺🇸 when the browser locale is US, otherwise 🇬🇧.
const getSettingsLanguageFlag = (code) => {
  if (code === 'en') {
    try {
      if (typeof navigator !== 'undefined') {
        const navLang = (navigator.language || navigator.userLanguage || '').toLowerCase();
        if (navLang.includes('en-us') || navLang.endsWith('-us')) {
          return '🇺🇸';
        }
      }
    } catch (e) {}
    return '🇬🇧';
  }
  const entry = LANGUAGES.find(lang => lang.code === code);
  return entry?.flag ?? '';
};

function App() {
  const { t, i18n } = useTranslation();
  const [latestWord, setLatestWord] = useState(null);
  const [showPreviousWords, setShowPreviousWords] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSettingsPage, setShowSettingsPage] = useState(false);
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [cookieConsent, setCookieConsent] = useState(null); // null = not set, 'functional' | 'none'
  const [consentInitialized, setConsentInitialized] = useState(false); // true after we've read consent from localStorage
  const [geoRegion, setGeoRegion] = useState(null); // null = loading, 'eu' | 'non-eu'
  const [showInformationalBanner, setShowInformationalBanner] = useState(false);
  const [showPrivacyNote, setShowPrivacyNote] = useState(false);
  const [showLicensesModal, setShowLicensesModal] = useState(false);
  const [mobileStore, setMobileStore] = useState(null); // 'android' | 'ios' | null
  const [showAppInstallBanner, setShowAppInstallBanner] = useState(false);
  const [hintWarningMessage, setHintWarningMessage] = useState(null);
  const [showLearnHowToPlayButton, setShowLearnHowToPlayButton] = useState(true);
  const geoFetchedRef = useRef(false);
  const [dayWinners, setDayWinners] = useState({});
  const [storageCleared, setStorageCleared] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState(null); // Track duplicate word message
  const [highlightedAlreadyGuessedWord, setHighlightedAlreadyGuessedWord] = useState(null); // Timeline row to highlight after Enter on already-guessed word
  const [pendingGuess, setPendingGuess] = useState(null); // Track word that's temporarily shown below input
  const [pendingWord, setPendingWord] = useState(null); // Track the word string that's pending
  const [justMovedWord, setJustMovedWord] = useState(null); // Word that just moved (landing slot in list; hidden while flying)
  const [flyingWord, setFlyingWord] = useState(null); // { guessData, fromRect, toRect?, animateToFinal? } – visible move from temp to list
  const [showKeepGuessing, setShowKeepGuessing] = useState(false); // Delayed so it doesn't overlap with the just-guessed chip
  const [hintStarRowCue, setHintStarRowCue] = useState(false); // Latched after explainable pending flies away until next submit
  const [showHintStarCueDelayed, setShowHintStarCueDelayed] = useState(false); // Same 550ms delay + styling as "keep guessing"
  const [hintExplanationPopup, setHintExplanationPopup] = useState(null); // { word: string, text: string } when open
  // Initialize displayedGuesses from guesses immediately to prevent initial shift
  const [displayedGuesses, setDisplayedGuesses] = useState(() => {
    // This will be set properly in useEffect, but initialize to prevent undefined
    return [];
  });
  const lastSubmittedWordRef = useRef(null); // Track the word that was just submitted
  const isInitializedRef = useRef(false); // Track if displayed guesses have been initialized
  const temporaryPlaceholderRef = useRef(null); // Rect of pending guess chip (start of move)
  const justMovedItemRef = useRef(null); // List item DOM (end of move) for measuring toRect
  const prevPendingGuessForHintCueRef = useRef(null);
  const guessesRef = useRef([]);
  
  // Ensure language is always set (default to 'en' if not initialized yet)
  // Normalize language code (e.g., 'en-US' -> 'en', 'fr-FR' -> 'fr')
  const rawLanguage = i18n.language || 'en';
  const currentLanguage = rawLanguage.split('-')[0]; // Extract base language code
  
  const {
    word,
    loading,
    result,
    error,
    guesses,
    notFoundWords,
    vocabularyCheck,
    checkingVocabulary,
    clickableSuggestions,
    winningWord,
    isYesterdayMode,
    activeDayKey,
    setWord,
    setClickableSuggestions,
    setVocabularyCheck,
    handleSubmit,
    applySuggestion: applySuggestionOriginal,
    reportValidWord,
    reportedValidWord,
    reportValidWordLoading,
    requestHint,
    hintLoading,
    hintsUsed,
    hintLimit,
    lastHintAtGuessCount,
    hintExplanationsByWord,
    playPreviousDay,
    playToday,
  } = useSemantle(process.env.REACT_APP_API_URL || '', currentLanguage); // Pass current language to useSemantle

  useEffect(() => {
    guessesRef.current = guesses;
  }, [guesses]);
  
  // Wrapper for applySuggestion that checks for duplicates
  const applySuggestion = async (suggestedWord, language = null) => {
    const wordToCheck = suggestedWord.trim().toLowerCase();
    // Check if word is already in guesses
    const isDuplicate = guesses.some(g => g.word.toLowerCase() === wordToCheck);
    if (isDuplicate) {
      setVocabularyCheck(null);
      setHighlightedAlreadyGuessedWord(wordToCheck);
      setDuplicateMessage(wordToCheck);
      setTimeout(() => {
        setDuplicateMessage(null);
      }, 1000);
      return;
    }
    setHintStarRowCue(false);
    setShowHintStarCueDelayed(false);
    // Track the word we're about to submit (same as onSubmit) so the pending-guess
    // effect can show it and add it to displayedGuesses when the result comes back
    lastSubmittedWordRef.current = wordToCheck;
    setLatestWord(wordToCheck);
    setPendingWord(wordToCheck);
    // If not duplicate, proceed with original function
    await applySuggestionOriginal(suggestedWord, language);
  };

  const countdown = useCountdown();

  const COOKIE_CONSENT_KEY = 'logiword_cookie_consent';

  // Read cookie consent from storage on mount (web only). Mark initialized so we don't show intro before we know.
  useEffect(() => {
    try {
      if (typeof localStorage !== 'undefined') {
        const v = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (v === 'functional' || v === 'none') setCookieConsent(v);
      }
      setConsentInitialized(true);
    } catch (e) {
      setConsentInitialized(true);
    }
  }, []);

  // Firebase Analytics: sync Measurement consent with cookie choice
  useEffect(() => {
    if (!consentInitialized) return;
    syncWebAnalyticsConsent(cookieConsent);
  }, [cookieConsent, consentInitialized]);

  const analyticsOpenedRef = useRef(false);
  useEffect(() => {
    if (cookieConsent !== 'functional') return;
    if (analyticsOpenedRef.current) return;
    analyticsOpenedRef.current = true;
    (async () => {
      await logScreenView('GameScreen');
      await logEvent('app_opened', { language: i18n.language });
    })();
  }, [cookieConsent, i18n.language]);

  useEffect(() => {
    if (cookieConsent !== 'functional') return;
    if (word === null && !loading) {
      logEvent(GameEvents.GAME_STARTED, {
        language: i18n.language,
        is_yesterday_mode: isYesterdayMode,
      });
    }
  }, [word, loading, isYesterdayMode, i18n.language, cookieConsent]);

  // Geo-based consent: EU/EEA/UK = opt-in; rest = enabled by default + informational banner
  useEffect(() => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
    if (cookieConsent !== null) return; // Already chose or was set by geo
    if (geoFetchedRef.current) return;

    geoFetchedRef.current = true;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), GEO_TIMEOUT_MS);

    fetch(GEO_API_URL, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error('geo failed');
        return res.json();
      })
      .then((data) => {
        const country = (data && data.country_code) ? String(data.country_code).toUpperCase() : '';
        const isEU = EU_EEA_UK_COUNTRY_CODES.has(country);
        if (isEU) {
          setGeoRegion('eu');
        } else {
          setGeoRegion('non-eu');
          // DEBUG: skip auto-accept so everyone sees combined intro+cookie screen; revert for production
          // try {
          //   localStorage.setItem(COOKIE_CONSENT_KEY, 'functional');
          //   setCookieConsent('functional');
          //   if (!localStorage.getItem(INFO_BANNER_SEEN_KEY)) {
          //     setShowInformationalBanner(true);
          //   }
          // } catch (e) {}
        }
      })
      .catch(() => {
        setGeoRegion('eu'); // Timeout or error: require consent (strict)
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });
  }, [cookieConsent]);

  // Show intro modal only after we've read storage and consent is still null (user must choose). Never auto-show after they've already accepted (reload = no intro).
  useEffect(() => {
    try {
      if (!consentInitialized) return; // Wait until we've read localStorage so we don't show intro then flash to "Got it" after consent is restored
      if (cookieConsent === null) {
        setShowIntroModal(true); // DEBUG: everyone sees combined screen; for production use: cookieConsent === null && geoRegion === 'eu'
      }
    } catch (e) {}
  }, [cookieConsent, geoRegion, consentInitialized]);

  const changeLanguage = (lng) => {
    const from = i18n.language;
    i18n.changeLanguage(lng);
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem(COOKIE_CONSENT_KEY) === 'functional') {
        localStorage.setItem('i18nextLng', lng);
      }
    } catch (e) {}
    logEvent(GameEvents.LANGUAGE_CHANGED, { from_language: from, to_language: lng });
  };

  const acceptCookies = () => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, 'functional');
      setCookieConsent('functional');
      setShowPrivacyNote(false);
    } catch (e) {}
  };

  const declineCookies = async () => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, 'none');
      setCookieConsent('none');
      setShowPrivacyNote(false);
      await clearAllStorage();
      localStorage.removeItem('logiword_hasSeenIntro');
      localStorage.removeItem('i18nextLng');
      localStorage.removeItem('clickableSuggestions');
      setShowIntroModal(true);
      playToday();
    } catch (e) {}
  };

  const handleClearStorage = async () => {
    const success = await clearAllStorage();
    if (success) {
      try {
        localStorage.removeItem('logiword_hasSeenIntro');
      } catch (e) {}
      setShowIntroModal(true);
      setStorageCleared(true);
      playToday();
      setTimeout(() => setStorageCleared(false), 3000);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const wordToSubmit = word.trim().toLowerCase();
    
    // If already guessed: don't process at all — only show message and stop
    const isDuplicate = guesses.some(g => g.word.toLowerCase() === wordToSubmit);
    if (isDuplicate) {
      setHighlightedAlreadyGuessedWord(wordToSubmit);
      setDuplicateMessage(wordToSubmit);
      setWord('');
      setTimeout(() => setDuplicateMessage(null), 1000);
      return;
    }

    setHighlightedAlreadyGuessedWord(null);
    setHintStarRowCue(false);
    setShowHintStarCueDelayed(false);
    // Track the word we're about to submit
    lastSubmittedWordRef.current = wordToSubmit;
    setLatestWord(wordToSubmit);
    // Mark word as pending immediately
    setPendingWord(wordToSubmit);
    await handleSubmit(currentLanguage);
  };
  

  // Sync displayed guesses with guesses (e.g. clear when language switches, then show new language's guesses).
  useEffect(() => {
    if (guesses.length === 0) {
      setDisplayedGuesses([]);
      setPendingGuess(null);
      setPendingWord(null);
      setJustMovedWord(null);
      setFlyingWord(null);
      setLatestWord(null);
      setHighlightedAlreadyGuessedWord(null);
      setDuplicateMessage(null);
      setHintStarRowCue(false);
      setShowHintStarCueDelayed(false);
      return;
    }
    const excludeWord = (lastSubmittedWordRef.current || '').toLowerCase();
    const filtered = guesses.filter(g => g.word.toLowerCase() !== excludeWord).map(g => ({ ...g }));
    if (!isInitializedRef.current) {
      setDisplayedGuesses(filtered);
      isInitializedRef.current = true;
    } else if (displayedGuesses.length === 0 && guesses.length > 0) {
      setDisplayedGuesses(filtered);
    }
  }, [guesses.length, guesses, displayedGuesses.length]);

  // Track pending guess when result comes back with a valid word (only for the word we actually submitted, not current input)
  useEffect(() => {
    if (result && result.inVocab && typeof result.score === 'number') {
      const submittedWord = lastSubmittedWordRef.current;
      if (submittedWord) {
        const guessData = guesses.find(g => g.word.toLowerCase() === submittedWord);
        if (guessData) {
          logEvent(GameEvents.WORD_GUESSED, {
            language: i18n.language,
            similarity: result.similarity,
            percentile: result.percentile,
          });
          // Show in temporary place, then move visibly to list after 0.75s (same for first and subsequent guesses)
          setPendingWord(prev => prev || submittedWord);
          setPendingGuess(guessData);
          const wordMovingToList = submittedWord;
          const timer = setTimeout(() => {
            const fromRect = temporaryPlaceholderRef.current?.getBoundingClientRect?.();
            const latestList = guessesRef.current || [];
            const latestGuess =
              latestList.find((g) => g.word.toLowerCase() === wordMovingToList.toLowerCase()) || guessData;
            setPendingGuess(null);
            setPendingWord(null);
            lastSubmittedWordRef.current = null;
            setJustMovedWord(wordMovingToList);
            setDisplayedGuesses([...latestList]);
            if (fromRect) {
              setFlyingWord({ guessData: latestGuess, fromRect });
            }
          }, 750);
          return () => clearTimeout(timer);
        }
      }
    } else if (result && !result.inVocab) {
      // If word is not valid, clear pending word immediately
      setPendingWord(null);
      lastSubmittedWordRef.current = null;
    }
  }, [result, guesses, i18n.language]);

  useEffect(() => {
    if (cookieConsent !== 'functional') return;
    if (result && result.congratulations && winningWord) {
      logEvent(GameEvents.GAME_COMPLETED, {
        language: i18n.language,
        winning_word: winningWord,
        total_guesses: guesses.length,
        is_yesterday_mode: isYesterdayMode,
      });
    }
  }, [result, winningWord, guesses.length, i18n.language, isYesterdayMode, cookieConsent]);

  useEffect(() => {
    if (cookieConsent !== 'functional') return;
    if (showSettings && !showSettingsPage) {
      logEvent(GameEvents.SETTINGS_OPENED, { language: i18n.language });
    }
  }, [showSettings, showSettingsPage, i18n.language, cookieConsent]);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    let detectedStore = null;
    try {
      const userAgent = (navigator.userAgent || '').toLowerCase();
      const platform = (navigator.platform || '').toLowerCase();
      const maxTouchPoints = navigator.maxTouchPoints || 0;

      const isAndroid = /android/.test(userAgent);
      const isIOS = /iphone|ipad|ipod/.test(userAgent) || (platform === 'macintel' && maxTouchPoints > 1);

      if (isAndroid) {
        detectedStore = 'android';
      } else if (isIOS) {
        detectedStore = 'ios';
      }
    } catch (e) {
      detectedStore = null;
    }

    setMobileStore(detectedStore);

    if (!detectedStore) return;
    try {
      if (typeof localStorage !== 'undefined') {
        const dismissed = localStorage.getItem(APP_INSTALL_BANNER_DISMISSED_KEY) === '1';
        setShowAppInstallBanner(!dismissed);
      } else {
        setShowAppInstallBanner(true);
      }
    } catch (e) {
      setShowAppInstallBanner(true);
    }
  }, []);

  const dismissAppInstallBanner = () => {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(APP_INSTALL_BANNER_DISMISSED_KEY, '1');
      }
    } catch (e) {}
    setShowAppInstallBanner(false);
  };

  const renderAppInstallBanner = () => {
    if (!showAppInstallBanner || !mobileStore) return null;
    const isAndroid = mobileStore === 'android';
    const href = isAndroid ? PLAY_STORE_URL : APP_STORE_URL;
    const helperText = isAndroid ? t('installBannerAndroidHelper') : t('installBannerIosHelper');
    const badgeSrc = isAndroid ? PLAY_STORE_BADGE_URL : APP_STORE_BADGE_URL;
    const badgeAlt = isAndroid ? t('installBannerPlayStoreAlt') : t('installBannerAppStoreAlt');

    return (
      <div
        role="region"
        aria-label="Install app"
        style={{
          width: '100%',
          maxWidth: '720px',
          margin: '72px auto 16px',
          padding: '10px 14px',
          borderRadius: '12px',
          backgroundColor: 'rgba(18, 18, 24, 0.94)',
          border: '1px solid rgba(255, 255, 255, 0.45)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          boxSizing: 'border-box'
        }}
      >
        <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)' }}>
          {helperText}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '2px',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              color: '#111827',
              textDecoration: 'none',
              fontSize: '13px',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            <img
              src={badgeSrc}
              alt={badgeAlt}
              style={{
                height: isAndroid ? '44px' : '36px',
                width: 'auto',
                display: 'block',
                borderRadius: '6px'
              }}
            />
          </a>
          <button
            type="button"
            onClick={dismissAppInstallBanner}
            aria-label="Dismiss install app prompt"
            style={{
              border: '1px solid rgba(255, 255, 255, 0.3)',
              background: 'transparent',
              color: 'rgba(255,255,255,0.85)',
              borderRadius: '8px',
              width: '30px',
              height: '30px',
              lineHeight: '28px',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>
      </div>
    );
  };

  // Measure list item position (toRect) after displayedGuesses and justMovedWord update
  useEffect(() => {
    if (!justMovedWord || !flyingWord || flyingWord.toRect) return;
    const raf = requestAnimationFrame(() => {
      const el = justMovedItemRef.current;
      if (el) {
        const toRect = el.getBoundingClientRect();
        setFlyingWord(prev => prev ? { ...prev, toRect } : null);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [justMovedWord, flyingWord]);

  // Start moving flying word to final position after toRect is set
  useEffect(() => {
    if (!flyingWord?.toRect || flyingWord.animateToFinal) return;
    const raf = requestAnimationFrame(() => {
      setFlyingWord(prev => prev ? { ...prev, animateToFinal: true } : null);
    });
    return () => cancelAnimationFrame(raf);
  }, [flyingWord?.toRect, flyingWord?.animateToFinal]);

  // After an explainable-hint pending chip clears, show the ★ hint line in the keep-guessing slot until the next submit.
  useEffect(() => {
    const prev = prevPendingGuessForHintCueRef.current;
    if (prev && !pendingGuess && getHintExplanationForGuess(prev, hintExplanationsByWord)) {
      setHintStarRowCue(true);
    }
    prevPendingGuessForHintCueRef.current = pendingGuess;
  }, [pendingGuess, hintExplanationsByWord]);

  // "Keep guessing" vs hint cue: same 550ms delay after pending clears (chip finished moving) before showing.
  useEffect(() => {
    const eligible = guesses.length > 0 && !pendingGuess && (!vocabularyCheck || !vocabularyCheck.suggestions || vocabularyCheck.suggestions.length === 0);
    if (!eligible) {
      setShowKeepGuessing(false);
      setShowHintStarCueDelayed(false);
      return;
    }
    if (hintStarRowCue) {
      setShowKeepGuessing(false);
      setShowHintStarCueDelayed(false);
      const t = setTimeout(() => setShowHintStarCueDelayed(true), 550);
      return () => clearTimeout(t);
    }
    setShowHintStarCueDelayed(false);
    const t = setTimeout(() => setShowKeepGuessing(true), 550);
    return () => clearTimeout(t);
  }, [guesses.length, pendingGuess, vocabularyCheck?.suggestions?.length, hintStarRowCue]);

  useEffect(() => {
    if (!hintExplanationPopup) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setHintExplanationPopup(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hintExplanationPopup]);

  // Memoize timeline content to prevent re-renders
  // Use displayedGuesses state which only updates after delay, not guesses array
  const timelineContent = useMemo(() => {
    if (displayedGuesses.length === 0) {
      return null;
    }
    
    return displayedGuesses.map((guess, index) => {
      const score = typeof guess.score === 'number' ? guess.score : 0;
      const displayed = getDisplayedScore(score);
      const wordColor = getColorFromPercentile(displayed);
      
      // Check if this is the latest word (fade to normal when user starts typing)
      const isLatest = latestWord && guess.word.toLowerCase() === latestWord && !word.trim();
      // Highlight this row when user hit Enter on an already-guessed word (stays until next guess)
      const isHighlightedAlreadyGuessed = highlightedAlreadyGuessedWord && guess.word.toLowerCase() === highlightedAlreadyGuessedWord;
      const isJustMoved = justMovedWord && guess.word.toLowerCase() === justMovedWord.toLowerCase();
      // Hide list row while the flying copy exists (no double). Clear flyingWord before justMovedWord
      // on transition end (rAF) so the row can paint before the overlay unmounts—avoids a blank frame.
      const isLandingSlot = isJustMoved && flyingWord;
      const hintExplanationText = getHintExplanationForGuess(guess, hintExplanationsByWord);

      return (
        <div
          key={guess.word} // Use word as key for stability (words are unique)
          ref={isJustMoved ? justMovedItemRef : undefined}
          onClick={hintExplanationText ? () => setHintExplanationPopup({ word: guess.word, text: hintExplanationText }) : undefined}
          style={{
            padding: '8px 12px',
            backgroundColor: (isLatest || isHighlightedAlreadyGuessed) ? 'rgba(59, 130, 246, 0.3)' : 'rgba(0, 0, 0, 0.34)',
            borderRadius: '8px',
            border: `2px solid ${wordColor}`,
            boxShadow: `0 0 15px ${wordColor}40, 0 4px 6px rgba(0, 0, 0, 0.3)`,
            minWidth: '120px',
            textAlign: 'left',
            transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease',
            backdropFilter: 'blur(10px)',
            opacity: isLandingSlot ? 0 : 1,
            pointerEvents: isLandingSlot ? 'none' : 'auto',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = `0 0 25px ${wordColor}80, 0 6px 12px rgba(0, 0, 0, 0.4)`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = `0 0 15px ${wordColor}40, 0 4px 6px rgba(0, 0, 0, 0.3)`;
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
          }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '4px',
              flexShrink: 0,
            }}
            >
              <div style={{
                color: wordColor,
                fontWeight: 'bold',
                fontSize: '16px',
              }}
              >
                {guess.word}
              </div>
              {hintExplanationText ? (
                <span
                  aria-hidden
                  style={{
                    color: 'rgba(250, 204, 21, 0.95)',
                    fontSize: '15px',
                    lineHeight: 1,
                    verticalAlign: 'middle',
                    userSelect: 'none',
                  }}
                >
                  ★
                </span>
              ) : null}
            </div>
            <div style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '12px',
            }}
            >
              {formatDisplayedScore(score)}
            </div>
          </div>
        </div>
      );
    });
  }, [displayedGuesses, latestWord, word, highlightedAlreadyGuessedWord, getColorFromPercentile, getDisplayedScore, formatDisplayedScore, justMovedWord, flyingWord, hintExplanationsByWord, t]);

  const renderTimeline = () => {
    if (!timelineContent || timelineContent.length === 0) {
      return null;
    }
    
    return (
      <div 
        id="timeline-container"
        style={{ 
          marginTop: '0', 
          paddingTop: '8px',
          paddingBottom: '8px',
          width: '100%', 
          maxWidth: '800px',
          position: 'relative', // Ensure stable positioning
          contain: 'layout style paint', // CSS containment to prevent layout shifts
          transform: 'translateZ(0)', // Force GPU acceleration for smoother rendering
          backfaceVisibility: 'hidden', // Prevent flickering
          isolation: 'isolate', // Create new stacking context to prevent layout shifts
          flexShrink: 0 // Prevent timeline from shrinking
        }}
      >
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          width: '100%',
          minHeight: '0', // Prevent layout shifts
          willChange: 'contents', // Optimize for stable rendering
          transform: 'translateZ(0)', // Force GPU acceleration
          backfaceVisibility: 'hidden', // Prevent flickering
          position: 'relative', // Ensure stable positioning
          flexShrink: 0 // Prevent shrinking
        }}>
          {/* Words stacked vertically, sorted by score (high to low) */}
          {timelineContent}
        </div>
      </div>
    );
  };

  // Load winning words for all days when showing previous words page
  useEffect(() => {
    if (showPreviousWords) {
      const loadWinners = async () => {
        const pastDays = getPastDays(7);
        const winners = {};
        for (const dayKey of pastDays) {
          try {
            const winningWord = await loadWinningWord(dayKey, currentLanguage);
            if (winningWord) {
              winners[dayKey] = winningWord;
            }
          } catch (err) {}
        }
        setDayWinners(winners);
      };
      loadWinners();
    } else {
      // Clear winners when closing the page
      setDayWinners({});
    }
  }, [showPreviousWords, currentLanguage]);

  // Render drawer menu overlay and menu
  const renderDrawerMenu = () => {
    if (!showSettings || showSettingsPage) return null;
    
    return (
      <>
        <div 
          className="drawer-overlay"
          onClick={() => {
            setShowSettings(false);
            setShowSettingsPage(false);
          }}
        />
        <div className={`drawer-menu ${showSettings ? 'open' : ''}`}>
          <div className="drawer-content">
            <button
              onClick={() => {
                setShowSettings(false);
                setShowPreviousWords(false);
                playToday();
              }}
              className="drawer-item"
            >
              {t('home')}
            </button>
            <button
              onClick={() => setShowSettingsPage(true)}
              className="drawer-item"
            >
              {t('settings')}
            </button>
            <button
              onClick={() => {
                setShowSettings(false);
                setShowPreviousWords(true);
              }}
              className="drawer-item"
            >
              {t('playPreviousWords')}
            </button>
            <button
              onClick={() => {
                setShowSettings(false);
                setShowIntroModal(true);
              }}
              className="drawer-item"
            >
              {t('howToPlay')}
            </button>
          </div>
        </div>
      </>
    );
  };

  const handleCloseIntro = useCallback(() => {
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem(COOKIE_CONSENT_KEY) === 'functional') {
        localStorage.setItem('logiword_hasSeenIntro', '1');
      }
    } catch (e) {}
    setShowIntroModal(false);
  }, []);

  const handleIntroAcceptCookies = () => {
    acceptCookies();
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem('logiword_hasSeenIntro', '1');
    } catch (e) {}
    setShowIntroModal(false);
  };

  const handleIntroDeclineCookies = () => {
    declineCookies();
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem('logiword_hasSeenIntro', '1');
    } catch (e) {}
    setShowIntroModal(false);
  };

  const renderIntroModal = () => {
    if (!showIntroModal) return null;
    const highColor = getColorFromPercentile(98);
    const lowColor = getColorFromPercentile(3);
    const showCookieChoice = cookieConsent === null; // Web: combine how-to-play with cookie choice for EU
    return (
      <div
        className="intro-modal-overlay"
        onClick={showCookieChoice ? undefined : handleCloseIntro}
        role="dialog"
        aria-modal="true"
        aria-labelledby="intro-modal-title"
      >
        <div
          className="intro-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 id="intro-modal-title" className="intro-modal-title">
            {t('introTitle')}
          </h2>
          <div className="intro-modal-scroll">
            <p className="intro-modal-body">
              {t('introBody')}
              <span style={{ color: getColorFromPercentile(100), fontWeight: 'bold' }}>{t('introExampleWordPizza')}</span>
              {t('introExampleIntroAfter')}
            </p>
            <div
              className="intro-modal-chip"
              style={{ borderColor: highColor, boxShadow: `0 0 12px ${highColor}40` }}
            >
              <span style={{ color: highColor, fontWeight: 'bold' }}>{t('introExampleHighWord')}</span>
              <span className="intro-modal-chip-score">98.5%</span>
            </div>
            <p className="intro-modal-body">{t('introExampleLow')}</p>
            <div
              className="intro-modal-chip"
              style={{ borderColor: lowColor, boxShadow: `0 0 12px ${lowColor}40` }}
            >
              <span style={{ color: lowColor, fontWeight: 'bold' }}>{t('introExampleLowWord')}</span>
              <span className="intro-modal-chip-score">2.1%</span>
            </div>
            <p className="intro-modal-body intro-modal-body-outro">{t('introExampleOutro')}</p>
          </div>
          {showCookieChoice ? (
            <div
              style={{
                borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                backgroundColor: 'rgba(28, 25, 30, 0.98)',
                padding: '16px 20px 20px',
                boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                borderRadius: '0 0 12px 12px'
              }}
            >
              <p style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: '14px', lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                {t('cookieConsentInfoMessage')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={() => setShowPrivacyNote(true)}
                  style={{
                    padding: '10px 14px',
                    fontSize: '14px',
                    background: 'none',
                    border: 'none',
                    color: 'rgba(255,255,255,0.7)',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  {t('cookieConsentPrivacyNote')}
                </button>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginLeft: 'auto' }}>
                  <button
                    type="button"
                    onClick={handleIntroDeclineCookies}
                    style={{
                      padding: '10px 20px',
                      fontSize: '15px',
                      fontWeight: '500',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'transparent',
                      color: 'rgba(255,255,255,0.9)',
                      cursor: 'pointer'
                    }}
                  >
                    {t('cookieConsentDecline')}
                  </button>
                  <button
                    type="button"
                    onClick={handleIntroAcceptCookies}
                    style={{
                      padding: '10px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'rgba(74, 222, 128, 0.25)',
                      color: '#4ade80',
                      cursor: 'pointer'
                    }}
                  >
                    {t('cookieConsentAccept')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="intro-modal-button"
              onClick={handleCloseIntro}
            >
              {t('introGotIt')}
            </button>
          )}
        </div>
      </div>
    );
  };

  const dismissInformationalBanner = () => {
    try {
      localStorage.setItem(INFO_BANNER_SEEN_KEY, '1');
    } catch (e) {}
    setShowInformationalBanner(false);
  };

  const renderCookieBanner = () => (
    <div
      role="dialog"
      aria-label={t('cookieConsentTitle')}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        backgroundColor: 'rgba(28, 25, 30, 0.98)',
        borderTop: '1px solid rgba(255, 255, 255, 0.15)',
        padding: '16px 20px 20px',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      <p style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: '14px', lineHeight: 1.45 }}>
        {t('cookieConsentMessage')}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
        <button
          type="button"
          onClick={declineCookies}
          style={{
            padding: '10px 20px',
            fontSize: '15px',
            fontWeight: '500',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            background: 'transparent',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer'
          }}
        >
          {t('cookieConsentDecline')}
        </button>
        <button
          type="button"
          onClick={acceptCookies}
          style={{
            padding: '10px 20px',
            fontSize: '15px',
            fontWeight: '600',
            borderRadius: '8px',
            border: 'none',
            background: 'rgba(74, 222, 128, 0.25)',
            color: '#4ade80',
            cursor: 'pointer'
          }}
        >
          {t('cookieConsentAccept')}
        </button>
        <button
          type="button"
          onClick={() => setShowPrivacyNote(true)}
          style={{
            padding: '10px 14px',
            fontSize: '14px',
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.7)',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          {t('cookieConsentPrivacyNote')}
        </button>
      </div>
    </div>
  );

  const renderInformationalBanner = () => (
    <div
      role="dialog"
      aria-label={t('cookieConsentTitle')}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        backgroundColor: 'rgba(28, 25, 30, 0.98)',
        borderTop: '1px solid rgba(255, 255, 255, 0.15)',
        padding: '16px 20px 20px',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      <p style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: '14px', lineHeight: 1.45 }}>
        {t('cookieConsentInfoMessage')}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
        <button
          type="button"
          onClick={dismissInformationalBanner}
          style={{
            padding: '10px 20px',
            fontSize: '15px',
            fontWeight: '600',
            borderRadius: '8px',
            border: 'none',
            background: 'rgba(74, 222, 128, 0.25)',
            color: '#4ade80',
            cursor: 'pointer'
          }}
        >
          {t('cookieConsentOk')}
        </button>
        <button
          type="button"
          onClick={() => { setShowPrivacyNote(true); }}
          style={{
            padding: '10px 14px',
            fontSize: '14px',
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.7)',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          {t('cookieConsentPrivacyNote')}
        </button>
      </div>
    </div>
  );

  const renderHintWarningModal = () => {
    if (!hintWarningMessage) return null;
    return (
      <div
        className="intro-modal-overlay"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="hint-warning-title"
        onClick={() => setHintWarningMessage(null)}
      >
        <div
          className="intro-modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: '360px' }}
        >
          <h2 id="hint-warning-title" className="intro-modal-title" style={{ fontSize: '1.1rem', marginBottom: '12px' }}>
            💡 {t('hint')}
          </h2>
          <p className="intro-modal-body" style={{ marginBottom: '16px' }}>
            {hintWarningMessage}
          </p>
          <button
            type="button"
            className="intro-modal-button"
            onClick={() => setHintWarningMessage(null)}
          >
            {t('introGotIt')}
          </button>
        </div>
      </div>
    );
  };

  const renderPrivacyNoteModal = () => (
    <div
      className="intro-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-note-title"
      onClick={() => setShowPrivacyNote(false)}
    >
      <div
        className="intro-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '480px' }}
      >
        <h2 id="privacy-note-title" className="intro-modal-title">
          {t('privacyNoteTitle')}
        </h2>
        <div className="intro-modal-scroll">
          <p className="intro-modal-body" style={{ whiteSpace: 'pre-line' }}>
            {t('privacyNoteBody')}
          </p>
          <p style={{ marginTop: '12px' }}>
            <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#61dafb' }}>
              {t('privacyPolicyLink')}
            </a>
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '16px' }}>
          <button
            type="button"
            onClick={declineCookies}
            style={{
              padding: '10px 20px',
              fontSize: '15px',
              fontWeight: '500',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              background: 'transparent',
              color: 'rgba(255,255,255,0.9)',
              cursor: 'pointer'
            }}
          >
            {t('cookieConsentDecline')}
          </button>
          <button
            type="button"
            onClick={acceptCookies}
            style={{
              padding: '10px 20px',
              fontSize: '15px',
              fontWeight: '600',
              borderRadius: '8px',
              border: 'none',
              background: 'rgba(74, 222, 128, 0.25)',
              color: '#4ade80',
              cursor: 'pointer'
            }}
          >
            {t('cookieConsentAccept')}
          </button>
          <button
            type="button"
            onClick={() => setShowPrivacyNote(false)}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.7)',
              cursor: 'pointer'
            }}
          >
            {t('back')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderLicensesModal = () => (
    <div
      className="intro-modal-overlay licenses-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="licenses-modal-title"
      onClick={() => setShowLicensesModal(false)}
    >
      <div
        className="intro-modal-content intro-modal-content--licenses"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="licenses-modal-title" className="intro-modal-title">
          {t('licensesModalTitle')}
        </h2>
        <div className="intro-modal-scroll">
          <p className="intro-modal-body" style={{ lineHeight: 1.6, marginBottom: '16px' }}>
            {t('licensesModalIntro')}
          </p>
          {THIRD_PARTY_NOTICES.map((notice, idx) => (
            <section
              key={idx}
              style={{
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: idx < THIRD_PARTY_NOTICES.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none'
              }}
            >
              <h3 style={{ fontSize: '0.95rem', fontWeight: '600', color: 'rgba(255,255,255,0.95)', margin: '0 0 6px 0' }}>
                {notice.name}
              </h3>
              {notice.copyright && (
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)', margin: '0 0 4px 0', lineHeight: 1.5 }}>
                  Copyright: {notice.copyright}
                </p>
              )}
              <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)', margin: '0 0 8px 0', lineHeight: 1.5 }}>
                License: {notice.license}
              </p>
              <p style={{ margin: 0, fontSize: '0.85rem' }}>
                <a href={notice.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#61dafb', marginRight: '12px' }}>
                  {t('licensesModalSource')}
                </a>
                <a href={notice.licenseUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#61dafb' }}>
                  {t('licensesModalFullLicense')}
                </a>
              </p>
            </section>
          ))}
        </div>
        <button
          type="button"
          className="intro-modal-button"
          onClick={() => setShowLicensesModal(false)}
        >
          {t('licensesModalClose')}
        </button>
      </div>
    </div>
  );

  // Show settings page (check this second)
  if (showSettings && showSettingsPage) {
    return (
      <div className="App">
        {renderIntroModal()}
        {cookieConsent === null && (geoRegion === 'eu' || geoRegion === null) && !showIntroModal && renderCookieBanner()}
        {showInformationalBanner && renderInformationalBanner()}
        {showPrivacyNote && renderPrivacyNoteModal()}
        {showLicensesModal && ReactDOM.createPortal(renderLicensesModal(), document.body)}
        {renderHintWarningModal()}
        <header className="App-header">
          {renderDrawerMenu()}
          {renderAppInstallBanner()}
          {/* Hamburger Menu Button */}
          <div style={{ 
            position: 'absolute', 
            top: '20px', 
            left: '20px',
            zIndex: 1001
          }}>
            <button
              onClick={() => {
                setShowSettingsPage(false);
                setShowSettings(true);
              }}
              className="hamburger-menu"
              aria-label={t('menu')}
            >
              <span className="hamburger-bar"></span>
              <span className="hamburger-bar"></span>
              <span className="hamburger-bar"></span>
            </button>
          </div>
          {/* Language Selector */}
          <div style={{ 
            position: 'absolute', 
            top: '20px', 
            right: '20px',
            zIndex: 1002
          }}>
            <LanguageSelector />
          </div>

          {/* Settings content above background layer (z-index: 1 like main content) */}
          <div style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            maxWidth: '480px',
            margin: '0 auto',
            padding: '24px 20px',
            minHeight: '60vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: '24px'
          }}>
            <h1 style={{
              color: '#ffffff',
              fontSize: '1.75rem',
              fontWeight: '600',
              margin: '0 0 8px 0',
              letterSpacing: '-0.02em'
            }}>
              {t('settings')}
            </h1>

            {/* Language section */}
            <section style={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              padding: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              <span style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.8rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {t('selectLanguage')}
              </span>
              <select
                value={currentLanguage}
                onChange={(e) => changeLanguage(e.target.value)}
                style={{
                  padding: '12px 14px',
                  fontSize: '15px',
                  width: '100%',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  color: '#fff',
                  outline: 'none',
                  cursor: 'pointer',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='rgba(255,255,255,0.8)' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  paddingRight: '36px'
                }}
              >
                {LANGUAGES.map((lang) => {
                  const flag = getSettingsLanguageFlag(lang.code);
                  return (
                    <option key={lang.code} value={lang.code} style={{ color: '#000', backgroundColor: '#fff' }}>
                      {flag ? `${flag} ${lang.name}` : lang.name}
                    </option>
                  );
                })}
              </select>
            </section>

            {/* Clickable Suggestions section */}
            <section style={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              padding: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <span style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.8rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {t('clickableSuggestions') || 'Clickable Suggestions'}
              </span>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <button
                  type="button"
                  onClick={() => setClickableSuggestions(false)}
                  style={{
                    flex: 1,
                    minWidth: '100px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: '1px solid ' + (clickableSuggestions ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.5)'),
                    borderRadius: '8px',
                    background: clickableSuggestions ? 'transparent' : 'rgba(255, 255, 255, 0.15)',
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'background 0.2s, border-color 0.2s'
                  }}
                >
                  {t('disabled') || 'Disabled'}
                </button>
                <button
                  type="button"
                  onClick={() => setClickableSuggestions(true)}
                  style={{
                    flex: 1,
                    minWidth: '100px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: '1px solid ' + (clickableSuggestions ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.2)'),
                    borderRadius: '8px',
                    background: clickableSuggestions ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'background 0.2s, border-color 0.2s'
                  }}
                >
                  {t('enabled') || 'Enabled'}
                </button>
              </div>
            </section>

            {/* Testing: Clear all data (match mobile) */}
            <section style={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              padding: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <span style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.8rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {t('testing') || 'Testing'}
              </span>
              <button
                type="button"
                onClick={handleClearStorage}
                style={{
                  padding: '12px 20px',
                  fontSize: '15px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  backgroundColor: 'rgba(239, 68, 68, 0.2)',
                  color: '#fff',
                  cursor: 'pointer',
                  transition: 'background 0.2s, border-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.3)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                }}
              >
                {t('clearAllData') || 'Clear All Data'}
              </button>
              {storageCleared && (
                <span style={{ color: '#4ade80', fontSize: '14px' }}>
                  {t('dataCleared') || '✓ All data cleared successfully!'}
                </span>
              )}
            </section>

            {/* Licenses & acknowledgements */}
            <section style={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              padding: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <span style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.8rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {t('licensesAcknowledgements')}
              </span>
              <button
                type="button"
                onClick={() => setShowLicensesModal(true)}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  background: 'transparent',
                  color: '#61dafb',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                {t('licensesAcknowledgements')}
              </button>
            </section>

            {/* Privacy & Cookies */}
            <section style={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              padding: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <span style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.8rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {t('privacyCookies')}
              </span>
              <p style={{ color: 'rgba(255,255,255,0.85)', margin: 0, fontSize: '14px', lineHeight: 1.45 }}>
                {cookieConsent === 'functional' ? t('cookieConsentAccepted') : cookieConsent === 'none' ? t('cookieConsentDeclined') : cookieConsent === null ? t('cookieConsentNotSet') : t('cookieConsentFunctional')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={declineCookies}
                    style={{
                      padding: '10px 20px',
                      fontSize: '15px',
                      fontWeight: '500',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'transparent',
                      color: 'rgba(255,255,255,0.9)',
                      cursor: 'pointer'
                    }}
                  >
                    {t('cookieConsentDecline')}
                  </button>
                  <button
                    type="button"
                    onClick={acceptCookies}
                    style={{
                      padding: '10px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'rgba(74, 222, 128, 0.25)',
                      color: '#4ade80',
                      cursor: 'pointer'
                    }}
                  >
                    {t('cookieConsentAccept')}
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setShowPrivacyNote(true)}
                    style={{
                      padding: '10px 16px',
                      fontSize: '14px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'transparent',
                      color: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    {t('cookieConsentPrivacyNote')}
                  </button>
                  <a
                    href="/privacy-policy.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#61dafb', fontSize: '14px' }}
                  >
                    {t('privacyPolicyLink')}
                  </a>
                </div>
              </div>
            </section>
          </div>
        </header>
      </div>
    );
  }

  // Show previous words selection page (check this second)
  if (showPreviousWords) {
    const pastDays = getPastDays(7);
    const formatDate = (dayKey) => {
      const date = new Date(dayKey + 'T00:00:00');
      return date.toLocaleDateString(i18n.language, { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    };

    return (
      <div className="App">
        {renderIntroModal()}
        {cookieConsent === null && (geoRegion === 'eu' || geoRegion === null) && !showIntroModal && renderCookieBanner()}
        {showInformationalBanner && renderInformationalBanner()}
        {showPrivacyNote && renderPrivacyNoteModal()}
        {renderHintWarningModal()}
        <header className="App-header">
          {renderDrawerMenu()}
          {renderAppInstallBanner()}
          {/* Hamburger Menu Button */}
          <div style={{ 
            position: 'absolute', 
            top: '20px', 
            left: '20px',
            zIndex: 1001
          }}>
            <button
              onClick={() => {
                setShowSettingsPage(false);
                setShowSettings(true);
              }}
              className="hamburger-menu"
              aria-label={t('menu')}
            >
              <span className="hamburger-bar"></span>
              <span className="hamburger-bar"></span>
              <span className="hamburger-bar"></span>
            </button>
          </div>
          {/* Language Selector */}
          <div style={{ 
            position: 'absolute', 
            top: '20px', 
            right: '20px',
            zIndex: 1002
          }}>
            <LanguageSelector />
          </div>

          <div style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center',
            padding: '20px',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <h1 style={{
              color: '#ffffff',
              fontSize: '2em',
              fontWeight: 'bold',
              marginBottom: '30px'
            }}>
              {t('playPreviousWords') || 'Play Previous Words'}
            </h1>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              width: '100%'
            }}>
              {pastDays.map((dayKey, index) => {
                const isToday = index === 0;
                return (
                  <button
                    key={dayKey}
                    onClick={() => {
                      if (isToday) {
                        playToday();
                      } else {
                        logEvent(GameEvents.PREVIOUS_DAY_PLAYED, {
                          language: i18n.language,
                          day_key: dayKey,
                        });
                        playPreviousDay(dayKey);
                      }
                      setShowPreviousWords(false);
                    }}
                    style={{
                      padding: '16px 24px',
                      fontSize: '16px',
                      borderRadius: '8px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      backgroundColor: isToday 
                        ? 'rgba(74, 222, 128, 0.2)' 
                        : 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = isToday 
                        ? 'rgba(74, 222, 128, 0.3)' 
                        : 'rgba(255, 255, 255, 0.2)';
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = isToday 
                        ? 'rgba(74, 222, 128, 0.2)' 
                        : 'rgba(255, 255, 255, 0.1)';
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      fontWeight: 'bold', 
                      marginBottom: '4px' 
                    }}>
                      <span>{formatDate(dayKey)}</span>
                      {dayWinners[dayKey] && (
                        <span style={{ fontSize: '1.2em' }}>🎉</span>
                      )}
                    </div>
                    {isToday && (
                      <div style={{ fontSize: '12px', opacity: 0.8 }}>
                        {t('today') || 'Today'}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </header>
      </div>
    );
  }

  // Show congratulations screen if user won
  if (result && result.congratulations && winningWord) {
    return (
      <div className="App">
        {renderIntroModal()}
        {cookieConsent === null && (geoRegion === 'eu' || geoRegion === null) && !showIntroModal && renderCookieBanner()}
        {showInformationalBanner && renderInformationalBanner()}
        {showPrivacyNote && renderPrivacyNoteModal()}
        {renderHintWarningModal()}
        <header className="App-header">
          {renderDrawerMenu()}
          {/* Hamburger Menu Button */}
          <div style={{ 
            position: 'absolute', 
            top: '20px', 
            left: '20px',
            zIndex: 1001
          }}>
            <button
              onClick={() => {
                setShowSettingsPage(false);
                setShowSettings(true);
              }}
              className="hamburger-menu"
              aria-label={t('menu')}
            >
              <span className="hamburger-bar"></span>
              <span className="hamburger-bar"></span>
              <span className="hamburger-bar"></span>
            </button>
          </div>
          {/* Language Selector */}
          <div style={{ 
            position: 'absolute', 
            top: '20px', 
            right: '20px',
            zIndex: 1002
          }}>
            <LanguageSelector />
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            padding: '20px',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{
              textAlign: 'center',
              padding: '32px 40px',
              borderRadius: '16px',
              backgroundColor: 'rgba(0, 0, 0, 0.35)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              maxWidth: '600px'
            }}>
              <h1 style={{
                color: '#4ade80',
                fontSize: '2.5em',
                fontWeight: 'bold',
                marginBottom: '20px',
                textShadow: '0 0 20px rgba(74, 222, 128, 0.5)'
              }}>
                {t('congratulations')}
              </h1>
              <p style={{
                color: '#ffffff',
                fontSize: '1.5em',
                marginBottom: '40px',
                lineHeight: '1.6'
              }}>
                {isYesterdayMode
                ? (activeDayKey === getYesterdayDayKey()
                    ? t('youGuessedYesterdaysWord')
                    : t('youGuessedWordForDate', {
                        date: activeDayKey
                          ? new Date(activeDayKey + 'T00:00:00').toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })
                          : ''
                      }))
                : t('youGuessedTodaysWord')} <strong style={{ color: '#4ade80', fontSize: '1.2em' }}>{winningWord}</strong> {t('correctly')}
              </p>
              {!isYesterdayMode && (
                <div style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  padding: '16px 40px 12px',
                  borderRadius: '12px',
                  border: '2px solid rgba(74, 222, 128, 0.3)',
                  boxShadow: '0 0 30px rgba(74, 222, 128, 0.2)',
                  marginBottom: '30px'
                }}>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.9em',
                    margin: 0,
                    lineHeight: 1.2
                  }}>
                    {t('nextWordIn')}
                  </p>
                  <p style={{
                    color: '#4ade80',
                    fontSize: '1.6em',
                    fontWeight: 'bold',
                    fontFamily: 'monospace',
                    margin: 0,
                    lineHeight: 1.2
                  }}>
                    {countdown}
                  </p>
                </div>
              )}
              <button
                onClick={() => setShowPreviousWords(true)}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  border: '2px solid rgba(163, 230, 53, 0.7)',
                  backgroundColor: 'rgba(20, 35, 15, 0.75)',
                  color: 'rgba(212, 255, 160, 0.95)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 0 20px rgba(163, 230, 53, 0.3), 0 0 40px rgba(134, 239, 172, 0.12)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(30, 50, 22, 0.85)';
                  e.target.style.borderColor = 'rgba(163, 230, 53, 0.9)';
                  e.target.style.boxShadow = '0 0 24px rgba(163, 230, 53, 0.4), 0 0 48px rgba(134, 239, 172, 0.18)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(20, 35, 15, 0.75)';
                  e.target.style.borderColor = 'rgba(163, 230, 53, 0.7)';
                  e.target.style.boxShadow = '0 0 20px rgba(163, 230, 53, 0.3), 0 0 40px rgba(134, 239, 172, 0.12)';
                }}
              >
                {t('playPreviousWords')}
              </button>
            </div>
            <p style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '1.1em',
              marginTop: '24px',
              marginBottom: '12px',
              width: '100%',
              maxWidth: '800px',
              textAlign: 'center'
            }}>
              {t('yourGuessesWere')}
            </p>
            {renderTimeline()}
          </div>
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      {renderIntroModal()}
      {cookieConsent === null && (geoRegion === 'eu' || geoRegion === null) && !showIntroModal && renderCookieBanner()}
      {showInformationalBanner && renderInformationalBanner()}
      {showPrivacyNote && renderPrivacyNoteModal()}
      {renderHintWarningModal()}
      <header className="App-header">
        {renderDrawerMenu()}
        {renderAppInstallBanner()}
        {/* Hamburger Menu Button */}
        <div style={{ 
          position: 'absolute', 
          top: '20px', 
          left: '20px',
          zIndex: 1001
        }}>
          <button
            onClick={() => {
              setShowSettingsPage(false);
              setShowSettings(true);
            }}
            className="hamburger-menu"
            aria-label={t('menu')}
          >
            <span className="hamburger-bar"></span>
            <span className="hamburger-bar"></span>
            <span className="hamburger-bar"></span>
          </button>
        </div>
        {/* Language Selector */}
        <div style={{ 
          position: 'absolute', 
          top: '20px', 
          right: '20px',
          zIndex: 1002
        }}>
          <LanguageSelector />
        </div>

        {/* Main content wrapper: above background (::before z-index: 0) */}
        <div style={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Logo at top */}
        <div
          className="hero-logo-wrapper"
          style={{
          display: 'flex',
          justifyContent: 'center'
        }}>
          <img
            className="hero-logo-image"
            src={require('./assets/logo.png')}
            alt=""
            style={{
              height: '193px',
              maxWidth: '90%',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>
        <div style={{ maxWidth: '720px', width: '100%', textAlign: 'center', margin: '0 auto 14px' }}>
          <p style={{
            margin: 0,
            paddingTop: '6px',
            paddingBottom: '2px',
            fontSize: '0.98rem',
            lineHeight: 1.45,
            color: 'rgba(255, 255, 255, 0.82)'
          }}>
            {t('seoHomepageIntro')}
          </p>
        </div>

        {/* Flying word: visible move from temporary location to list (transform for smooth GPU animation) */}
        {flyingWord && flyingWord.fromRect && (() => {
          const from = flyingWord.fromRect;
          const to = flyingWord.toRect;
          const animateToFinal = flyingWord.animateToFinal && to;
          const dx = to ? to.left - from.left : 0;
          const dy = to ? to.top - from.top : 0;
          const fw = flyingWord.guessData;
          const wLower = (fw.word || '').toLowerCase();
          const isLatestFly = !!(latestWord && wLower === latestWord.toLowerCase() && !word.trim());
          const isHighlightedFly = !!(highlightedAlreadyGuessedWord && wLower === highlightedAlreadyGuessedWord.toLowerCase());
          const flyingRowBg = (isLatestFly || isHighlightedFly) ? 'rgba(59, 130, 246, 0.3)' : 'rgba(0, 0, 0, 0.34)';
          const flyingExpl = getHintExplanationForGuess(flyingWord.guessData, hintExplanationsByWord);
          return (
          <div
            style={{
              position: 'fixed',
              top: from.top,
              left: from.left,
              width: from.width,
              height: from.height,
              padding: '8px 12px',
              boxSizing: 'border-box',
              backgroundColor: flyingRowBg,
              borderRadius: '8px',
              border: `2px solid ${getColorFromPercentile(getDisplayedScore(flyingWord.guessData.score ?? 0))}`,
              boxShadow: `0 0 15px ${getColorFromPercentile(getDisplayedScore(flyingWord.guessData.score ?? 0))}40, 0 4px 6px rgba(0, 0, 0, 0.3)`,
              minWidth: '120px',
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '8px',
              pointerEvents: 'none',
              zIndex: 9999,
              willChange: to ? 'transform' : 'auto',
              transform: `translate(${animateToFinal ? dx : 0}px, ${animateToFinal ? dy : 0}px)`,
              transition: to ? 'transform 0.65s cubic-bezier(0.32, 0, 0.2, 1)' : 'none',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
            }}
            onTransitionEnd={(e) => {
              if (e.propertyName !== 'transform') return;
              setFlyingWord(null);
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  setJustMovedWord(null);
                });
              });
            }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '4px',
            }}
            >
              <div style={{ color: getColorFromPercentile(getDisplayedScore(flyingWord.guessData.score ?? 0)), fontWeight: 'bold', fontSize: '16px' }}>
                {flyingWord.guessData.word}
              </div>
              {flyingExpl ? (
                <span
                  aria-hidden
                  style={{
                    color: 'rgba(250, 204, 21, 0.95)',
                    fontSize: '15px',
                    lineHeight: 1,
                    userSelect: 'none',
                  }}
                >
                  ★
                </span>
              ) : null}
            </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px' }}>
              {formatDisplayedScore(flyingWord.guessData.score ?? 0)}
            </div>
          </div>
          );
        })()}

        {hintExplanationPopup && typeof document !== 'undefined' && ReactDOM.createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="hint-explanation-dialog-title"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10060,
              background: 'rgba(18, 14, 32, 0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 20,
            }}
            onClick={() => setHintExplanationPopup(null)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setHintExplanationPopup(null);
            }}
          >
            <div
              role="document"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: 460,
                width: '100%',
                background: '#f7f4fc',
                color: '#1e1a2e',
                padding: '20px 22px 16px',
                borderRadius: 14,
                boxShadow: '0 12px 36px rgba(40, 32, 72, 0.2)',
                border: '1px solid #dcd4ec',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <span
                  id="hint-explanation-dialog-title"
                  style={{
                    fontSize: '1.35rem',
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: '#4f3d9e',
                    lineHeight: 1.35,
                  }}
                >
                  {hintExplanationPopup.word}
                </span>
                <span
                  aria-hidden="true"
                  style={{
                    color: 'rgba(250, 204, 21, 0.95)',
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    lineHeight: 1,
                  }}
                >
                  ★
                </span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: '0.98rem',
                  lineHeight: 1.55,
                  color: 'rgba(38, 34, 58, 0.9)',
                }}
              >
                {hintExplanationPopup.text}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                <button
                  type="button"
                  onClick={() => setHintExplanationPopup(null)}
                  style={{
                    margin: 0,
                    padding: '6px 14px',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    borderRadius: 999,
                    border: '1px solid #b8a8d8',
                    background: '#ede8f7',
                    color: '#3d2f6e',
                    cursor: 'pointer',
                  }}
                >
                  {t('introGotIt')}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {isYesterdayMode && (
          <div style={{
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <p style={{
              color: 'rgba(255, 255, 255, 0.65)',
              fontSize: '0.875rem',
              fontWeight: '500',
              margin: 0
            }}>
              {activeDayKey === getYesterdayDayKey()
                ? t('guessYesterdaysWord')
                : t('guessWordForDate', {
                    date: activeDayKey
                      ? new Date(activeDayKey + 'T00:00:00').toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })
                      : ''
                  })}
            </p>
          </div>
        )}
        <div
          style={{
            height: showLearnHowToPlayButton ? '58px' : '0px',
            marginBottom: showLearnHowToPlayButton ? '18px' : '0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            transition: 'height 240ms ease, margin-bottom 240ms ease'
          }}
        >
          <button
            type="button"
            onClick={() => {
              setShowIntroModal(true);
              setShowLearnHowToPlayButton(false);
            }}
            style={{
              margin: 0,
              padding: '14px 28px',
              fontSize: '16px',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.9)',
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              borderRadius: '24px',
              cursor: 'pointer',
              transition: 'background 0.2s ease, border-color 0.2s ease, opacity 180ms ease',
              opacity: showLearnHowToPlayButton ? 1 : 0,
              pointerEvents: showLearnHowToPlayButton ? 'auto' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            }}
          >
            💡 {t('learnHowToPlay')}
          </button>
        </div>
        <form onSubmit={onSubmit}>
          <div style={{ width: '100%', minWidth: '400px', maxWidth: '720px', position: 'relative' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                alignItems: 'center',
                height: '44px',
                padding: '0 14px'
              }}>
                <input
                  type="text"
                  className={guesses.length === 0 && !word.trim() ? 'input-start-hint' : ''}
                  placeholder={t('placeholder')}
                  value={word}
                  onChange={(e) => setWord(e.target.value)}
                  style={{
                    height: '44px',
                    padding: '0 16px',
                    margin: 0,
                    fontSize: '16px',
                    lineHeight: '44px',
                    width: '100%',
                    boxSizing: 'border-box',
                    borderRadius: '22px',
                    border: vocabularyCheck && !vocabularyCheck.exactMatch
                      ? '2px solid #ff6b6b'
                      : '1px solid rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    outline: 'none',
                    flex: 1,
                    minWidth: 0
                  }}
                />
                <button
                  type="submit"
                  disabled={!word.trim()}
                  style={{
                    height: '44px',
                    padding: '0 24px',
                    margin: 0,
                    fontSize: '16px',
                    lineHeight: '44px',
                    boxSizing: 'border-box',
                    borderRadius: '22px',
                    border: 'none',
                    backgroundColor: !word.trim() ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    cursor: !word.trim() ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  {t('submit')}
                </button>
                {!winningWord && (() => {
                  const canUseHint = hintsUsed < hintLimit && (lastHintAtGuessCount < 0 ? guesses.length >= 10 : guesses.length >= lastHintAtGuessCount + 10);
                  const hintWordsNeeded = !canUseHint && hintsUsed < hintLimit
                    ? (lastHintAtGuessCount < 0 ? 10 - guesses.length : lastHintAtGuessCount + 10 - guesses.length)
                    : 0;
                  return (
                    <button
                      type="button"
                      disabled={loading || hintLoading || hintsUsed >= hintLimit}
                      title={!canUseHint && hintsUsed < hintLimit ? (lastHintAtGuessCount < 0 ? t('hintGuessXWords', { count: hintWordsNeeded }) : t('hintGuessXMoreWords', { count: hintWordsNeeded })) : hintsUsed >= hintLimit ? t('hintsLimitReached') : undefined}
                      onClick={() => {
                        const canUse = hintsUsed < hintLimit && (lastHintAtGuessCount < 0 ? guesses.length >= 10 : guesses.length >= lastHintAtGuessCount + 10);
                        if (!canUse) {
                          const msg = hintsUsed >= hintLimit ? t('hintsLimitReached') : (lastHintAtGuessCount < 0 ? t('hintGuessXWords', { count: 10 - guesses.length }) : t('hintGuessXMoreWords', { count: lastHintAtGuessCount + 10 - guesses.length }));
                          setHintWarningMessage(msg);
                          return;
                        }
                        if (hintsUsed === hintLimit - 2) {
                          setHintWarningMessage(t('oneHintRemaining'));
                        }
                        setHintStarRowCue(false);
                        setShowHintStarCueDelayed(false);
                        requestHint((hintWord) => {
                          lastSubmittedWordRef.current = hintWord;
                          setLatestWord(hintWord);
                          setPendingWord(hintWord);
                        });
                      }}
                      style={{
                        height: '44px',
                        padding: '0 20px',
                        margin: 0,
                        fontSize: '16px',
                        lineHeight: '44px',
                        boxSizing: 'border-box',
                        borderRadius: '22px',
                        border: (loading || hintLoading || !canUseHint) ? '1px solid rgba(255, 255, 255, 0.35)' : '1px solid rgba(255, 200, 100, 0.6)',
                        backgroundColor: loading || hintLoading || !canUseHint ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 193, 7, 0.45)',
                        color: 'white',
                        cursor: loading || hintLoading || hintsUsed >= hintLimit ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      {hintLoading ? t('submitting') : (<><span style={{ filter: !canUseHint ? 'grayscale(100%)' : undefined }}>💡</span> {t('hint')}</>)}
                    </button>
                  );
                })()}
              </div>
              <div style={{ height: '20px', minHeight: '20px', marginTop: '4px', paddingTop: '6px', fontSize: '14px', overflow: 'hidden', boxSizing: 'border-box' }}>
                {vocabularyCheck && !vocabularyCheck.exactMatch && (
                  <span style={{ color: '#ff6b6b' }}>
                    {t('wordNotFound') || 'Word not in vocabulary'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </form>
        {/* Fixed space for pending guess or suggestions - fixed height so timeline never moves */}
        <div style={{
          marginTop: '12px',
          width: '100%',
          maxWidth: '500px',
          display: 'flex',
          justifyContent: 'center',
          height: (!pendingGuess && vocabularyCheck?.suggestions?.length > 0) ? '86px' : '52px',
          minHeight: (!pendingGuess && vocabularyCheck?.suggestions?.length > 0) ? '86px' : '52px',
          alignItems: 'center',
          flexShrink: 0,
          overflow: 'auto'
        }}>
          {pendingGuess && (() => {
            const score = typeof pendingGuess.score === 'number' ? pendingGuess.score : 0;
            const wordColor = getColorFromPercentile(getDisplayedScore(score));
            const pendingExpl = getHintExplanationForGuess(pendingGuess, hintExplanationsByWord);
            
            return (
              <div
                  ref={temporaryPlaceholderRef}
                  role={pendingExpl ? 'button' : undefined}
                  tabIndex={pendingExpl ? 0 : undefined}
                  aria-label={pendingExpl ? t('hintExplanationAria', { defaultValue: 'Hint explanation' }) : undefined}
                  title={pendingExpl ? t('hintExplanationAria', { defaultValue: 'Hint explanation' }) : undefined}
                  onClick={pendingExpl ? () => setHintExplanationPopup({ word: pendingGuess.word, text: pendingExpl }) : undefined}
                  onKeyDown={pendingExpl ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setHintExplanationPopup({ word: pendingGuess.word, text: pendingExpl });
                    }
                  } : undefined}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: 'rgba(59, 130, 246, 0.3)', // Same as latest word in timeline
                    borderRadius: '8px',
                    border: `2px solid ${wordColor}`,
                    boxShadow: `0 0 15px ${wordColor}40, 0 4px 6px rgba(0, 0, 0, 0.3)`,
                    minWidth: '120px',
                    textAlign: 'left',
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(10px)',
                    animation: 'fadeIn 0.15s ease',
                    cursor: pendingExpl ? 'pointer' : undefined,
                  }}
                >
                  <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'flex-start',
                  }}
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    >
                      <div style={{
                        color: wordColor,
                        fontWeight: 'bold',
                        fontSize: '16px',
                      }}
                      >
                        {pendingGuess.word}
                      </div>
                      {pendingExpl ? (
                        <span
                          aria-hidden
                          style={{
                            color: 'rgba(250, 204, 21, 0.95)',
                            fontSize: '15px',
                            lineHeight: 1,
                            userSelect: 'none',
                          }}
                        >
                          ★
                        </span>
                      ) : null}
                    </div>
                    <div style={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '12px',
                    }}
                    >
                      {formatDisplayedScore(score)}
                    </div>
                  </div>
                </div>
            );
          })()}
          {!pendingGuess && vocabularyCheck && vocabularyCheck.suggestions && vocabularyCheck.suggestions.length > 0 && (
            <div
              style={{
                width: '100%',
                maxHeight: '100%',
                padding: '8px 12px',
                backgroundColor: vocabularyCheck.exactMatch ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 107, 107, 0.2)',
                borderRadius: '8px',
                border: `2px solid ${vocabularyCheck.exactMatch ? 'rgba(255, 255, 255, 0.3)' : '#ff6b6b'}`,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                textAlign: 'left',
                animation: 'fadeIn 0.15s ease',
                overflow: 'auto'
              }}
            >
              <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', marginBottom: '6px' }}>
                {t('suggestions') || 'Suggestions'}:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                {vocabularyCheck.suggestions.slice(0, 5).map((suggestion, idx) => (
                  clickableSuggestions ? (
                    <span
                      key={idx}
                      onClick={() => applySuggestion(suggestion, currentLanguage)}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'; }}
                      onMouseLeave={(e) => { e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'; }}
                    >
                      {suggestion}
                    </span>
                  ) : (
                    <span key={idx} style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px' }}>
                      {suggestion}{idx < Math.min(4, vocabularyCheck.suggestions.length - 1) ? ', ' : ''}
                    </span>
                  )
                ))}
              </div>
              {!vocabularyCheck.exactMatch && (
                <div style={{ marginTop: '8px', fontSize: '13px' }}>
                  {reportedValidWord === vocabularyCheck.word ? (
                    <span style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{t('reportValidWordReported') || 'Reported'}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => reportValidWord()}
                      disabled={reportValidWordLoading}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'rgba(255, 255, 255, 0.9)',
                        textDecoration: 'underline',
                        cursor: reportValidWordLoading ? 'default' : 'pointer',
                        padding: 0,
                        fontSize: '13px'
                      }}
                    >
                      {reportValidWordLoading ? (t('checking') || '...') : (t('reportValidWord') || 'Report this word as valid')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {(() => {
            const slotForHintOrKeep =
              guesses.length > 0 &&
              !pendingGuess &&
              (!vocabularyCheck?.suggestions || vocabularyCheck.suggestions.length === 0);
            return (
              <>
                {slotForHintOrKeep && hintStarRowCue && showHintStarCueDelayed && renderHintClickStarCueRow(t)}
                {slotForHintOrKeep && !hintStarRowCue && showKeepGuessing && (
                  <span className="keep-guessing-text">{t('keepGuessing')}</span>
                )}
              </>
            );
          })()}
        </div>
        {/* Show duplicate message pop-up (fixed overlay so timeline doesn't move) */}
        {duplicateMessage && (
          <div style={{
            position: 'fixed',
            top: '96px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            maxWidth: '90vw',
            animation: 'fadeIn 0.3s ease'
          }}>
            <div style={{
              padding: '12px 20px',
              backgroundColor: 'rgba(239, 68, 68, 0.9)',
              borderRadius: '8px',
              border: '2px solid #ef4444',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              color: 'white',
              fontSize: '16px',
              fontWeight: '500',
              textAlign: 'center'
            }}>
              {`${t('wordAlreadyGuessed')}: "${duplicateMessage}"`}
            </div>
          </div>
        )}
        {/* Fixed-height strip for error/result so timeline never moves */}
        <div style={{ height: '8px', minHeight: '8px', marginTop: '0', width: '100%', maxWidth: '500px', overflow: 'hidden', lineHeight: '8px', fontSize: '13px' }}>
          {error && <div style={{ color: '#ff6b6b' }}>{t('error')}: {error === 'HINTS_LIMIT_REACHED' || error.startsWith('Maximum number of hints (') ? t('hintsLimitReached') : error.startsWith('HINT_NEED_GUESSES:') ? t('hintGuessXWords', { count: parseInt(error.split(':')[1], 10) }) : error.startsWith('HINT_NEED_MORE_GUESSES:') ? t('hintGuessXMoreWords', { count: parseInt(error.split(':')[1], 10) }) : error}</div>}
          {result && !result.congratulations && !result.inVocab && (
            <div style={{ color: 'rgba(255,255,255,0.9)' }}>
              {result.message || t('wordNotFound')}
            </div>
          )}
        </div>
        {renderTimeline()}
        {notFoundWords.length > 0 && (
          <div style={{ marginTop: '20px', width: '100%', maxWidth: '500px' }}>
            <h3 style={{ marginBottom: '10px' }}>{t('wordsNotFound')}</h3>
            <ul style={{ listStyle: 'none', padding: 0, textAlign: 'left' }}>
              {notFoundWords.map((word, index) => (
                <li 
                  key={`${word}-${index}`} 
                  style={{ 
                    padding: '8px', 
                    margin: '4px 0',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '4px',
                    color: 'rgba(255, 255, 255, 0.7)'
                  }}
                >
                  {word}
                </li>
              ))}
            </ul>
          </div>
        )}
        </div>
      </header>
    </div>
  );
}

export default App;

// npm 